import React, { useCallback, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
interface Page {
  src: string;
  label: string;
}
const pages: Page[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/uHL7pktKYxU3NxMmfS6rCw/volume_1_(15).webp',
  label: 'Volume 1: Foundation Phase — Essential Basketball Fundamentals'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/3mLCh4QYqG5uR7P9BE68U5/volume_2_(16).webp',
  label: 'Volume 2: Skill Development Phase — Complete Individual Skills'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/j3fobYj59HZGGpXkDZGoBx/volume_3_(33).webp',
  label: 'Volume 3: Game Intelligence Phase — Basketball IQ And Decision-Making'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/3TXbvopPn3pXF8eSGDRCHA/volume_4_(21).webp',
  label: 'Volume 4: Team Development Phase — Connected Team Basketball'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/j8r96USME4fZp6EWHJN4Bv/volume_5_(9).webp',
  label: 'Volume 5: The 365 Practice Vault — Ready-To-Use Practice Plans'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/vahcw4V6xPAPsBEz4cmDW6/volume_6_(10).webp',
  label: 'Volume 6: Coach Success System — Season Leadership And Management'
}];
const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0
  })
};
export function InsidePreview() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const current = pages[index];
  const paginate = useCallback((newDirection: number) => {
    setState(([previous]) => [(previous + newDirection + pages.length) % pages.length, newDirection]);
  }, []);
  const goTo = useCallback((target: number) => {
    setState(([previous]) => [target, target > previous ? 1 : -1]);
  }, []);
  return <section className="relative overflow-hidden border-t border-navy-800 bg-navy-950 py-12 md:py-24" aria-roledescription="carousel" aria-label="Look inside Basketball Ready: System 365" data-id="element-100">
      <div className="pointer-events-none absolute inset-0 bg-stadium-glow opacity-40" data-id="element-101" />
      <div className="container relative z-10 mx-auto px-4" data-id="element-102">
        <div className="mx-auto mb-10 max-w-3xl text-center md:mb-14" data-id="element-103">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-500/15 text-green-400 md:mb-6 md:h-16 md:w-16" data-id="element-104">
            <Eye className="h-6 w-6 md:h-8 md:w-8" data-id="element-105" />
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-4 font-anton text-3xl uppercase tracking-wide text-white sm:text-4xl md:text-6xl" data-id="element-106">
            LOOK INSIDE THE SYSTEM
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-gray-300 sm:text-xl" data-id="element-107">
            See exactly what you get — real sessions, drills, and coaching tools
            from inside all 6 volumes.
          </motion.p>
        </div>

        <div className="mx-auto max-w-[300px] px-0 md:max-w-md md:px-2" data-id="element-108">
          <div className="relative flex items-center justify-center" data-id="element-109">
            <button onClick={() => paginate(-1)} aria-label="Previous page" className="absolute left-1.5 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-navy-700 bg-navy-800 text-white shadow-lg transition-colors hover:border-gold-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 md:left-0 md:h-12 md:w-12 md:-translate-x-1/2" data-id="element-110">
              <ChevronLeft className="h-6 w-6" data-id="element-111" />
            </button>

            <div className="relative aspect-[2/3] w-full overflow-hidden rounded-2xl border border-navy-700 bg-navy-900 shadow-2xl" data-id="element-112">
              <div className="pointer-events-none absolute -inset-2 bg-gold-500/10 blur-2xl" data-id="element-113" />
              <AnimatePresence custom={direction} mode="popLayout" initial={false} data-id="element-114">
                <motion.img key={index} src={current.src} alt={current.label} loading="lazy" decoding="async" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.35,
                ease: 'easeInOut'
              }} className="absolute inset-0 h-full w-full object-contain" data-id="element-115" />
              </AnimatePresence>
            </div>

            <button onClick={() => paginate(1)} aria-label="Next page" className="absolute right-1.5 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-navy-700 bg-navy-800 text-white shadow-lg transition-colors hover:border-gold-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 md:right-0 md:h-12 md:w-12 md:translate-x-1/2" data-id="element-116">
              <ChevronRight className="h-6 w-6" data-id="element-117" />
            </button>
          </div>

          <div className="mt-5 min-h-[3rem] text-center sm:mt-6" data-id="element-118">
            <span className="font-oswald text-xs uppercase tracking-wide text-gold-500 sm:text-sm sm:tracking-widest" data-id="element-119">
              Page {index + 1} of {pages.length}
            </span>
            <p className="mt-1 text-sm font-medium leading-5 text-gray-300" aria-live="polite" data-id="element-120">
              {current.label}
            </p>
          </div>

          <div className="mt-5 flex items-center justify-center gap-2 sm:mt-6 sm:gap-3" data-id="element-121">
            {pages.map((page, pageIndex) => <button key={page.label} onClick={() => goTo(pageIndex)} aria-label={`Go to page ${pageIndex + 1}`} aria-current={pageIndex === index} className={`h-2 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 md:h-2.5 ${pageIndex === index ? 'w-6 bg-gold-500 md:w-8' : 'w-2 bg-navy-700 hover:bg-navy-600 md:w-2.5'}`} data-id="element-122" />)}
          </div>
        </div>
      </div>
    </section>;
}